import { useCallback, useMemo, useState } from "react";

import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import { graphql, VariablesOf } from "relay-runtime";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import UFOSegment from "@atlaskit/react-ufo/segment";

import {
    confluenceTemplatesUnifiedRelayPageQuery,
    confluenceTemplatesUnifiedRelayPageQuery$data
} from "@/__generated__/confluenceTemplatesUnifiedRelayPageQuery.graphql";
import { getIcons } from "@/components/confluence/templates/get-icons";
import { TemplateSectionWrapper } from "@/components/confluence/templates/template-section-wrapper";
import { ConfluenceTemplateAnalytics } from "@/components/confluence/templates/util/confluence-templates-analytics";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { useLocale } from "@/libraries/web-framework";
import { SharedHeaderFooterContainer } from "@/templates/software/confluence/templates/confluence-templates-page/src/sections/shared-header-footer-container";
import { partnerTemplatesDataUrlSlugs } from "@/templates/software/confluence/templates/confluence-templates-page/src/test-data/test-data";
import { useLocation } from "@/utils/location-provider";

import {
    AdditionalSlugs,
    CategoryOrder,
    DisplayedSlugs
} from "../config/category-template-order";

import { TemplateCategories } from "./components/template-categories-relay";
import { CategorySearchSuggestion } from "./components/template-category-search-results-relay";
import { TemplateSearchSuggestion } from "./components/template-fuzzy-search-results-relay";
import { parseSearchQuery } from "./components/template-initial-search-results-relay";
import {
    DEFAULT_CATEGORY_SLUG,
    parseCategorySlug,
    useCategorySlug
} from "./hooks/use-category-slug";
import { parseQueryLocale } from "./hooks/use-query-locale";
import {
    SectionDivider,
    TemplateCarouselSection
} from "./sections/template-carousel-section-relay";
import { TemplateFuzzySearch } from "./sections/template-fuzzy-search-relay";
import { TemplateHeroSection } from "./sections/template-hero-section-relay";
import { TemplateLearnFromLeadersSection } from "./sections/template-learn-from-leaders-section-relay";
import { TemplateSearchResultsSection } from "./sections/template-search-results-section-relay";

const parseSearchTerm = (params: string) => {
    const search = new URLSearchParams(params);
    return search.get("term");
};

const slugOrder: { [key: string]: number } = AdditionalSlugs.reduce(
    (
        acc: {
            [key: string]: number;
        },
        slug: string,
        index: number
    ) => {
        acc[slug] = index;
        return acc;
    },
    {}
);

const useAdditionalCategories = (
    categories: confluenceTemplatesUnifiedRelayPageQuery$data["categories"]
) => {
    const templateCategories = useMemo(() => {
        return categories.edges
            .map(({ node: { context } }) => context)
            .filter((category) => AdditionalSlugs.includes(category.urlSlug))
            .sort(
                (a, b) =>
                    (slugOrder[a.urlSlug] ?? 0) - (slugOrder[b.urlSlug] ?? 0)
            );
    }, [categories]);
    return templateCategories;
};

const useSortedCategories = (
    categories: confluenceTemplatesUnifiedRelayPageQuery$data["categories"]
) => {
    const adjustedCategories = useMemo(() => {
        return categories.edges
            .map(({ node: { context } }) => ({
                ...context,
                ...getIcons(context.urlSlug)
            }))
            .filter((category) => DisplayedSlugs.includes(category.urlSlug))
            .sort((a, b) => {
                const indexA =
                    CategoryOrder[a.urlSlug as keyof typeof CategoryOrder] ??
                    Number.MAX_VALUE;
                const indexB =
                    CategoryOrder[b.urlSlug as keyof typeof CategoryOrder] ??
                    Number.MAX_VALUE;
                return indexA - indexB;
            });
    }, [categories]);

    return adjustedCategories;
};

export const ConfluenceTemplatesUnifiedRelayPageQuery = graphql`
    query confluenceTemplatesUnifiedRelayPageQuery(
        $locale: String!
        $partnerTemplatesDataUrlSlugs: [String!]!
        $searchQuery: ContentPlatformContentQueryInput!
        $isSearch: Boolean = false
    ) {
        collections: templateCollections(
            search: { queries: { first: 50, withLocales: [$locale] } }
        ) {
            ...collectionCardCarouselRelayFragment
        }
        categories: contentFacet(
            first: 25
            forFields: ["category"]
            forContentType: "Template"
            withLocales: [$locale]
        ) {
            edges {
                node {
                    context
                }
            }
        }

        partnerTemplates: templates(
            search: {
                queries: {
                    withLocales: [$locale]
                    where: {
                        fieldNamed: "urlSlug"
                        havingValues: $partnerTemplatesDataUrlSlugs
                    }
                }
            }
        ) {
            ...partnerTemplateCardCarouselRelayFragment
        }

        ...templatePaginatedSearchResultsRelayRefetchableFragment
            @skip(if: $isSearch) # Skip preloading if doing a client-side search.
            @alias(as: "preloadedSearchResults")
            @arguments(searchQuery: $searchQuery)
    }
`;

export const getConfluenceTemplatesUnifiedRelayPageQueryVars = (
    location: Location,
    partialVars?: Partial<VariablesOf<confluenceTemplatesUnifiedRelayPageQuery>>
): VariablesOf<confluenceTemplatesUnifiedRelayPageQuery> => {
    const isSearch =
        partialVars?.isSearch ?? parseSearchTerm(location.search) !== null;

    const locale = partialVars?.locale ?? parseQueryLocale(location);
    return {
        locale,
        partnerTemplatesDataUrlSlugs:
            partialVars?.partnerTemplatesDataUrlSlugs ??
            partnerTemplatesDataUrlSlugs,
        searchQuery:
            partialVars?.searchQuery ?? isSearch
                ? {}
                : parseSearchQuery(location, { withLocales: [locale] }),
        isSearch
    };
};

const getRoute = (
    locale: string,
    searchTerm?:
        | string
        | CategorySearchSuggestion
        | TemplateSearchSuggestion
        | null
) => {
    let prefix = locale === "en" ? "" : `/${locale}`;
    prefix = `${prefix}/software/confluence/templates`;

    if (typeof searchTerm === "string") {
        return `${prefix}/search?term=${searchTerm}`;
    } else if (searchTerm?.type === "template" && searchTerm.name) {
        return `${prefix}/search?term=${searchTerm.name}`;
    } else if (
        searchTerm?.type === "category" &&
        searchTerm.id &&
        searchTerm.id !== DEFAULT_CATEGORY_SLUG
    ) {
        return `${prefix}/categories/${searchTerm.id}`;
    } else {
        return `${prefix}`;
    }
};

export type ConfluenceTemplatesUnifiedRelayPageTemplateProps = {
    queryRef: PreloadedQuery<confluenceTemplatesUnifiedRelayPageQuery>;
};

export const ConfluenceTemplatesUnifiedRelayPageTemplate = ({
    queryRef
}: ConfluenceTemplatesUnifiedRelayPageTemplateProps) => {
    const location = useLocation();
    const locale = useLocale();

    const categorySlug = useCategorySlug();

    const {
        categories,
        collections,
        partnerTemplates,
        preloadedSearchResults
    } = usePreloadedQuery<confluenceTemplatesUnifiedRelayPageQuery>(
        ConfluenceTemplatesUnifiedRelayPageQuery,
        queryRef
    );

    const categoryName = useMemo(() => {
        if (!categorySlug || categorySlug === DEFAULT_CATEGORY_SLUG) {
            return null;
        }
        const category = categories.edges.find(
            ({ node: { context } }) => context.urlSlug === categorySlug
        );
        return category?.node.context.name ?? null;
    }, [categories, categorySlug]);

    const [searchTerm, setSearchTerm] = useState<
        string | CategorySearchSuggestion | TemplateSearchSuggestion | null
    >(() => {
        let term = parseSearchTerm(location.search);
        if (!term) {
            term = parseCategorySlug(location.pathname);
            if (term && term !== DEFAULT_CATEGORY_SLUG) {
                return { type: "category", id: term };
            } else {
                return null;
            }
        }
        return term;
    });

    const [searchIntent, setSearchIntent] = useState<
        string | CategorySearchSuggestion | TemplateSearchSuggestion | null
    >(null);

    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleCategoryChange = useCallback(
        (id: string) => {
            const searchTerm: CategorySearchSuggestion = {
                type: "category",
                id
            };
            setSearchTerm(
                searchTerm.id === DEFAULT_CATEGORY_SLUG ? null : searchTerm
            );
            const url = getRoute(locale, searchTerm);

            history.replaceState(null, "", url);

            createAnalyticsEvent({
                action: "viewed",
                eventType: "screen",
                name: "page",
                attributes: {
                    category: id,
                    page_path: url,
                    originProduct: `wac-software-confluence-templates-categories-${id}`,
                    isMarketingEvent: true
                }
            }).fire();
            ConfluenceTemplateAnalytics.viewTemplatesTaxonomyCategory(id);
        },
        [createAnalyticsEvent, locale]
    );

    const handleCategoryChangeIntent = useCallback((id: string) => {
        setSearchIntent({ type: "category", id });
    }, []);

    const handleSearchSubmit = useCallback(
        (
            searchTerm:
                | string
                | CategorySearchSuggestion
                | TemplateSearchSuggestion
                | null
        ) => {
            setSearchTerm(searchTerm);
            history.replaceState(null, "", getRoute(locale, searchTerm));
        },
        [locale]
    );

    return (
        <div data-testid="confluence-templates-relay-page">
            <UFOSegment name="confluence-templates-relay">
                <SharedHeaderFooterContainer>
                    <main id="content">
                        <ContentNotFoundErrorBoundary>
                            <TemplateHeroSection categoryName={categoryName}>
                                <TemplateFuzzySearch
                                    onSubmit={handleSearchSubmit}
                                    value={
                                        typeof searchTerm === "string"
                                            ? searchTerm
                                            : null
                                    }
                                />
                            </TemplateHeroSection>
                            <TemplateSectionWrapper columnOrientation>
                                <TemplateCategories
                                    categories={useSortedCategories(categories)}
                                    onCategoryChange={handleCategoryChange}
                                    onCategoryChangeIntent={
                                        handleCategoryChangeIntent
                                    }
                                />
                                <TemplateSearchResultsSection
                                    preloadedSearchResults={
                                        preloadedSearchResults
                                    }
                                    searchTerm={searchTerm}
                                    searchIntent={searchIntent}
                                />
                            </TemplateSectionWrapper>
                            <SectionDivider />
                            <TemplateCarouselSection
                                collections={collections}
                            />
                            <TemplateLearnFromLeadersSection
                                templates={partnerTemplates}
                                categories={useAdditionalCategories(categories)}
                            />
                        </ContentNotFoundErrorBoundary>
                    </main>
                </SharedHeaderFooterContainer>
            </UFOSegment>
        </div>
    );
};
